import { type ChangeEvent, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';

import { AcceptAiFileExtensions } from '@/shared/features/ai-quiz-generation/constants';
import { isIos } from '@/utils';

import { useAiFileChange } from './useAiFileChange';

interface UseAiFileDroppableOptions {
  onAttachComplete: (file: File) => void;
  /**
   * 파일 첨부 전 guard. false 반환 시 첨부 중단.
   * validation(handleFileChange)보다 먼저 실행됨.
   *
   * @example
   * // upselling guard (landing flow)
   * beforeAttach: () => {
   *   upsellingModal.open({ initialPlanType: 'BASIC' });
   *   return false; // 항상 중단
   * }
   */
  beforeAttach?: () => boolean | Promise<boolean>;
}

export const useAiFileDroppable = ({
  onAttachComplete,
  beforeAttach,
}: UseAiFileDroppableOptions) => {
  const handleFileChange = useAiFileChange();

  const handleDropFile = useCallback(
    async (fileInput: File) => {
      // 1. Guard check FIRST (beforeAttach 미전달 시 skip)
      // BuildAiAutoTriggerModal은 항상 upselling modal을 열어야 함.
      // validation이 먼저 실행되면 사용자가 "잘못된 파일" 에러를 본 뒤
      // upselling modal을 보게 되는 UX 문제 발생.
      if (beforeAttach) {
        const canProceed = await beforeAttach();
        if (!canProceed) return;
      }

      // 2. 파일 유효성 검사 AFTER guard (기존 로직)
      const file = await handleFileChange(fileInput);
      if (!file) return;

      // 3. 첨부 완료
      onAttachComplete(fileInput);
    },
    [onAttachComplete, handleFileChange, beforeAttach],
  );

  const { inputRef, getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files: File[]) => {
      if (!files.length) {
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        return;
      }
      void handleDropFile(files[0]);
    },
    noClick: true,
    noKeyboard: true,
  });

  const { onChange, accept, ...rest } = getInputProps();

  return {
    inputRef,
    getRootProps,
    inputProps: {
      ...rest,
      accept: isIos() ? undefined : AcceptAiFileExtensions,
      //react state로 데이터 이관 후에, dom input value 초기화
      onChange: (event: ChangeEvent<HTMLInputElement>) => {
        onChange?.(event);
        event.target.value = '';
      },
    },
    isDragActive,
  };
};
